import type { RouteRecordName } from 'vue-router'
// TODO: Fix ignored tp/forbid-import-composable-to-service
// eslint-disable-next-line tp/using-vue-in-services-restriction,tp/forbid-import-composable-to-service
import type { Plugin } from '@/composition/vue/compositionApi'
import { gettingStartedRoutName } from '@/routes/user/getting-started/types'
import { tenDlcTollFreeRouteNames, TenDlcTollFreeRoutes } from '@/routes/types'
import { regulationsSettingsRouteNames } from '@/routes/user/services/types'
import type { TmCountryCode } from '@/services/types'
import type { OAuthProvider } from '@/services/domain/user/types'
import { UserSettingsRoutes } from '@/routes/user/settings'
import type { Nullable } from '@/types'

export interface Trackable {
  getPlugin(): Plugin
  getConfig(): TrackableConfig
  initializeTrackerAsPlugin(trackerAsPlugin: ExternalTrackerAsPlugin): Promise<void>
  cleanUp(): void
}

export interface TrackableConfig {
  id: string
  key?: string
  snippetVersion?: number | string
  host?: string
}

export interface ExternalTrackerAsPlugin {
  trackingHandler: () => Promise<void>
  src?: string
  id?: string
  loadOnCustomEvent?: boolean
  isDeferred?: boolean
}

export type InternalTrackingPayload = {
  eventName: string
  properties: Record<string, string>
}

export interface OpenAiClickIds {
  oppref: Nullable<string>
  obref: Nullable<string>
}

export interface TikTokClickIds {
  ttclid: Nullable<string>
}

// avoid reserved name https://developer.mozilla.org/en-US/docs/Web/API/TrackEvent
export type TrackingEvent = {
  name: TrackingEventName
  category: TrackingCategory
  properties?: Record<string, unknown>
}

export enum TrackingCategory {
  messenger = 'Messenger',
  payment = 'Payment',
}

export enum TrackingEventName {
  visitedPaymentPage = 'Visited Payment Page',
  selectedCreditAmount = 'Selected Credit Amount',
}

export const zendeskCountries: TmCountryCode[] = ['US', 'CA', 'PR', 'NZ', 'AU', 'GB', 'EE']
export const zendeskVisibleRoutes: string[] = [
  gettingStartedRoutName,
  ...tenDlcTollFreeRouteNames,
  ...regulationsSettingsRouteNames,
  'user.contacts.imports',
  'user.contacts.import',
  'user.contacts.imports.details.imported',
  'user.contacts.imports.details.duplicates',
  'user.contacts.imports.details.failed',
  'user.contacts.imports.details.excluded',
  'auth.forgotPassword',
  'auth.smsCode',
  'auth.emailCode',
  UserSettingsRoutes.BillingOverview,
  UserSettingsRoutes.BillingPlans,
]

export const hideZendeskWidgetBreakpoint = 1024

export type ZendeskMessagingToken = {
  token: string
}

export enum MntnConversionType {
  purchase = 'Purchase',
}

export enum GoogleAnalyticsEvent {
  signUp = 'OOLMCOvGwokBENfhgcgD',
  firstPayment = 'u1DxCLi8i4oBENfhgcgD',
  byocNumbersLink = 'zU0uCNve7LQZENfhgcgD',
}

export type GoogleAdsConversionOptions = {
  transactionId?: string
  newCustomer?: boolean
}

export enum PostHogEvent {
  pageView = '$pageview',
  signUp = 'sign_up',
  byocVisited = 'byoc_clicked',
  signUpFormCompleted = 'Completed Sign up form',
  signUpForFree = `Clicked "Sign up for free"`,
  signUpWithApple = `Clicked "Sign up with Apple"`,
  signUpWithMicrosoft = `Clicked "Sign up with Microsoft"`,
  signUpWithGoogle = `Clicked "Sign up with Google"`,
  tenDlcBrandVisited = `10DLC: Visited "Brand" registration`,
  tenDlcBrandSubmitted = `10DLC: Submitted "Brand" registration`,
  tenDlcCampaignVisited = `10DLC: Visited "Campaign" registration`,
  tenDlcCampaignSubmitted = `10DLC: Submitted "Campaign" registration`,
  tenDlcSummaryVisited = `10DLC: Visited "Summary" page`,
  tenDlcFormSubmitted = `10DLC: Submitted 10DLC registration form`,
  tenDlcClickedSkipAi = '10DLC: Clicked “Skip AI and fill manually”',
  tenDlcClickedAutoFillWithAi = '10DLC: Clicked “Auto-fill with AI”',
  tollFreeBrandVisited = `Toll-free: Visited "Brand" registration`,
  tollFreeBrandSubmitted = `Toll-free: Submitted "Brand" registration`,
  tollFreeCampaignVisited = `Toll-free: Visited "Campaign" registration`,
  tollFreeCampaignSubmitted = `Toll-free: Submitted "Campaign" registration`,
  tollFreeSummaryVisited = `Toll-free: Visited "Summary" page`,
  tollFreeFormSubmitted = `Toll-free: Submitted toll-free registration form`,
  tollFreeVisitedInfoPage = 'Toll-free: Visited “Info” page',
  tollFreeClickedSkipAi = 'Toll-free: Clicked “Skip AI and fill manually”',
  tollFreeClickedAutoFillWithAi = 'Toll-free: Clicked “Auto-fill with AI”',
  stripeBillingBuyCreditModalOpened = `Viewed "Buy credit" modal`,
  viewedAddBillingDetailsModal = `Viewed "Add billing details" modal`,
  clickedContinueWorking = `Clicked "Continue working"`,
  clickedCreateButton = `Clicked "Create" button`,
  pointAiEnabled = 'Point AI enabled',
}

export type PostHogRouteEvent = {
  routeName: RouteRecordName
  event: PostHogEvent
}

export const postHogRoutes: RouteRecordName[] = [UserSettingsRoutes.SmsVoiceByocFlow, ...tenDlcTollFreeRouteNames]

export const postHogRouteEvents: PostHogRouteEvent[] = [
  {
    routeName: UserSettingsRoutes.SmsVoiceByocFlow,
    event: PostHogEvent.byocVisited,
  },
  {
    routeName: TenDlcTollFreeRoutes.tenDlcRegisterBrand,
    event: PostHogEvent.tenDlcBrandVisited,
  },
  {
    routeName: TenDlcTollFreeRoutes.tenDlcRegisterCampaign,
    event: PostHogEvent.tenDlcCampaignVisited,
  },
  {
    routeName: TenDlcTollFreeRoutes.tenDlcSummary,
    event: PostHogEvent.tenDlcSummaryVisited,
  },
  {
    routeName: TenDlcTollFreeRoutes.tollFreeRegisterBrand,
    event: PostHogEvent.tollFreeBrandVisited,
  },
  {
    routeName: TenDlcTollFreeRoutes.tollFreeRegisterCampaign,
    event: PostHogEvent.tollFreeCampaignVisited,
  },
  {
    routeName: TenDlcTollFreeRoutes.tollFreeSummary,
    event: PostHogEvent.tollFreeSummaryVisited,
  },
]

export const postHogOAuthProviderEvents: Record<OAuthProvider, PostHogEvent> = {
  google: PostHogEvent.signUpWithGoogle,
  apple: PostHogEvent.signUpWithApple,
  microsoft: PostHogEvent.signUpWithMicrosoft,
}

export enum PostHogFeatureFlag {
  HomePageV1July = 'home-page-v1-july',
}

export enum NewHomePageVariant {
  VariantA = 'variant-A',
  Control = 'control',
}

export const postHogRegulationsEvents: Record<string, PostHogEvent> = {
  brand_upsertion: PostHogEvent.tenDlcBrandSubmitted,
  brand_submit: PostHogEvent.tenDlcBrandSubmitted,
  campaign_submission: PostHogEvent.tenDlcCampaignSubmitted,
  submission_for_review: PostHogEvent.tenDlcFormSubmitted,
  toll_free_brand_upsertion: PostHogEvent.tollFreeBrandSubmitted,
  toll_free_brand_submission: PostHogEvent.tollFreeBrandSubmitted,
  toll_free_campaign_submission: PostHogEvent.tollFreeCampaignSubmitted,
  toll_free_submission_for_review: PostHogEvent.tollFreeFormSubmitted,
}
